'use client';

import { useState } from 'react';

import { cn } from '@kit/ui/utils';

import type { MarketingLocale } from '~/lib/marketing/site';

import type { DemoKey } from '../../_lib/solutions.content';
import { BrandedAppDemo } from './branded-app-demo';
import { CalendarDemo } from './calendar-demo';
import { ClassBookingDemo } from './class-booking-demo';
import { CourtBookingDemo } from './court-booking-demo';
import { CrmDemo } from './crm-demo';
import { DemoMotionProvider, DemoPauseButton, FitScale } from './demo-kit';
import {
  ADMIN_DEMO_SIZE,
  BRANDED_APP_DEMO_SIZE,
  PAYMENTS_DEMO_SIZE,
  PHONE_SIZE,
} from './demo-sizes';
import { EnrollmentDemo } from './enrollment-demo';
import { GymAppDemo } from './gym-app-demo';
import { MembershipDemo } from './membership-demo';
import { PaymentsDemo } from './payments-demo';
import { WhatsAppDemo } from './whatsapp-demo';

type DemoComponent = React.ComponentType<{
  locale: MarketingLocale;
  className?: string;
}>;

interface DemoEntry {
  Component: DemoComponent;
  size: { width: number; height: number };
  kind: 'phone' | 'desktop' | 'composition';
}

export const DEMOS: Record<DemoKey, DemoEntry> = {
  'gym-app': { Component: GymAppDemo, size: PHONE_SIZE, kind: 'phone' },
  'court-booking': { Component: CourtBookingDemo, size: PHONE_SIZE, kind: 'phone' },
  'class-booking': { Component: ClassBookingDemo, size: PHONE_SIZE, kind: 'phone' },
  membership: { Component: MembershipDemo, size: PHONE_SIZE, kind: 'phone' },
  'branded-app': { Component: BrandedAppDemo, size: BRANDED_APP_DEMO_SIZE, kind: 'phone' },
  whatsapp: { Component: WhatsAppDemo, size: PHONE_SIZE, kind: 'phone' },
  calendar: { Component: CalendarDemo, size: ADMIN_DEMO_SIZE, kind: 'desktop' },
  crm: { Component: CrmDemo, size: ADMIN_DEMO_SIZE, kind: 'desktop' },
  enrollment: { Component: EnrollmentDemo, size: ADMIN_DEMO_SIZE, kind: 'desktop' },
  payments: { Component: PaymentsDemo, size: PAYMENTS_DEMO_SIZE, kind: 'composition' },
};

/**
 * Renders any product demo at its design size, scaled down to fit the
 * container. `maxScale` lets roomy layouts render desktop demos slightly
 * larger than their design size.
 */
export function ProductDemo({
  demo,
  locale,
  maxScale = 1,
  className,
}: {
  demo: DemoKey;
  locale: MarketingLocale;
  maxScale?: number;
  className?: string;
}) {
  const { Component, size } = DEMOS[demo];
  const [paused, setPaused] = useState(false);

  return (
    <div className={cn('relative', className)}>
      <DemoMotionProvider paused={paused}>
        <FitScale width={size.width} height={size.height} maxScale={maxScale}>
          <Component locale={locale} />
        </FitScale>
      </DemoMotionProvider>
      <DemoPauseButton
        paused={paused}
        onToggle={() => setPaused((value) => !value)}
        locale={locale}
        className="absolute -end-3 -top-3 z-20"
      />
    </div>
  );
}
